import { Navigate } from "react-router-dom"
import { useAppContext } from "../contexts/AppProvider"

// import Audit from "../pages/audit";
import DefaultLayout from "../layouts/DefaultLayout"
import Home from "../pages/home"
import IRoute from "../interfaces/IRoute"
import Login from "../pages/login"
import Logout from "../pages/logout"
import Project from "../pages/project"
import PureLayout from "../layouts/PureLayout"
import Templates from "../pages/admin/templates"
import Template from "../pages/admin/template"
import Users from "../pages/users"
import Can from "../components/Can"
import CommonForms from "../pages/admin/common-forms"
import CommonForm from "../pages/admin/common-form"
import Admin from "../pages/admin"
import AuditLogs from "../pages/admin/auditlogs"
import Audit from "../pages/audit"

// Stable component wrappers defined outside the hook so their references never
// change between renders. Inline arrow functions inside useRoutes/map would
// create new function identities on every App render, causing React Router to
// treat them as different component types and unmount/remount the entire page
// tree on every navigation — wiping all refs and local state.

const LoginRoute = () => <PureLayout component={Login} />
const LogoutRoute = () => <PureLayout component={Logout} />
const NotAllowedRoute = () => (
  <Navigate
    to="/login"
    state={{ redirectUrl: window.location.pathname }}
    replace
  />
)
const NotFoundRoute = () => <Navigate to="/" replace />

const HomeRoute = () => <Can action={undefined} no={() => <Navigate to="/" replace />} yes={() => <DefaultLayout component={Home} />} />
const TemplatesRoute = () => <Can action="projectTemplates.view" no={() => <Navigate to="/" replace />} yes={() => <DefaultLayout component={Templates} />} />
const AdminRoute = () => <Can action={undefined} no={() => <Navigate to="/" replace />} yes={() => <DefaultLayout component={Admin} />} />
const TemplateRoute = () => <Can action="projectTemplates.view" no={() => <Navigate to="/" replace />} yes={() => <DefaultLayout component={Template} />} />
const CommonFormsRoute = () => <Can action={undefined} no={() => <Navigate to="/" replace />} yes={() => <DefaultLayout component={CommonForms} />} />
const CommonFormRoute = () => <Can action="commonForms.view" no={() => <Navigate to="/" replace />} yes={() => <DefaultLayout component={CommonForm} />} />
const AuditLogsRoute = () => <Can action="auditLogs.viewAll" no={() => <Navigate to="/" replace />} yes={() => <DefaultLayout component={AuditLogs} />} />
const UsersRoute = () => <Can action="users.view" no={() => <Navigate to="/" replace />} yes={() => <DefaultLayout component={Users} />} />
const AuditRoute = () => <Can action={undefined} no={() => <Navigate to="/" replace />} yes={() => <DefaultLayout component={Audit} />} />
const ProjectRoute = () => <Can action={undefined} no={() => <Navigate to="/" replace />} yes={() => <DefaultLayout component={Project} />} />

// Routes visible for not signed in
const openRoutes: Array<IRoute> = [
  {
    path: "/login",
    key: "login",
    exact: true,
    component: LoginRoute,
    layout: PureLayout,
  },
  {
    path: "/logout",
    key: "logout",
    exact: true,
    component: LogoutRoute,
    layout: PureLayout,
  },
  {
    path: "*",
    key: "not-allowed",
    component: NotAllowedRoute,
    layout: PureLayout,
  },
]

// Routes visible for signed in
const protectedRoutes: Array<IRoute> = [
  {
    path: "/",
    key: "home",
    exact: true,
    component: HomeRoute,
    layout: DefaultLayout,
  },
  {
    path: "/admin/templates",
    key: "templates",
    exact: true,
    component: TemplatesRoute,
    layout: DefaultLayout,
    permission: "projectTemplates.view",
  },
  {
    path: "/admin",
    key: "admin",
    exact: true,
    component: AdminRoute,
    layout: DefaultLayout,
  },
  {
    path: "/admin/templates/:id",
    key: "template",
    exact: true,
    component: TemplateRoute,
    layout: DefaultLayout,
    permission: "projectTemplates.view",
  },
  {
    path: "/admin/common-forms",
    key: "common-forms",
    exact: true,
    component: CommonFormsRoute,
    layout: DefaultLayout,
  },
  {
    path: "/admin/common-forms/:id",
    key: "common-form",
    exact: true,
    component: CommonFormRoute,
    layout: DefaultLayout,
    permission: "commonForms.view",
  },
  {
    path: "/admin/audit-logs",
    key: "audit-logs",
    exact: true,
    component: AuditLogsRoute,
    layout: DefaultLayout,
    permission: "auditLogs.viewAll",
  },
  {
    path: "/admin/users",
    key: "users",
    exact: true,
    component: UsersRoute,
    layout: DefaultLayout,
    permission: "users.view",
  },
  {
    path: "/project/:_id/audit-log",
    key: "audit-log",
    exact: true,
    component: AuditRoute,
    layout: DefaultLayout,
  },
  {
    path: "/project/:_id/:page/:subsection?",
    key: "project",
    exact: true,
    component: ProjectRoute,
    layout: DefaultLayout,
  },
]

const useRoutes = () => {
  const { user } = useAppContext()

  if (!user) {
    return openRoutes
  }
  return [
    ...protectedRoutes,
    {
      path: "*",
      key: "not-found",
      component: NotFoundRoute,
      layout: DefaultLayout,
    },
  ]
}

export default useRoutes
